import { HEALTH } from "./UploadEvents";

/**
 * Pure health computation. Given a snapshot of an upload's current state,
 * returns one of the HEALTH.* enum values.
 *
 * Kept pure so it is:
 *   - trivially unit-testable (no timers, no side effects)
 *   - safely callable from React render paths (idempotent)
 *   - extensible: add a new health rule by editing this table and updating
 *     the enum + docs — no wiring elsewhere
 *
 * Rule ordering follows severity. First match wins.
 *
 * @param {object} input
 * @param {number} input.now                 current wall-clock ms
 * @param {number} input.lastProgressAt      ms of the last byte-progress event
 * @param {number} input.retryCount          total retry attempts so far
 * @param {number} [input.recentRetryCount]  retries within the last window
 * @param {number} [input.rollingBps]        current rolling throughput
 * @param {boolean} [input.online]           navigator.onLine
 * @param {boolean} [input.paused]
 * @param {string | null} [input.terminalError]
 * @param {"completed" | "uploading" | "created" | "paused" | "failed" | "cancelled" | "queued" | "finalizing"} [input.uploaderState]
 * @param {object}  [thresholds]             override defaults for tests
 * @returns {string}                         one of HEALTH.*
 */
export const computeHealth = (input, thresholds = DEFAULT_THRESHOLDS) => {
  if (!input) return HEALTH.HEALTHY;
  const {
    now,
    lastProgressAt,
    retryCount = 0,
    recentRetryCount,
    rollingBps,
    online = true,
    paused = false,
    terminalError,
    uploaderState,
  } = input;

  if (uploaderState === "completed") return HEALTH.COMPLETED;
  if (uploaderState === "cancelled") return HEALTH.CANCELLED;
  if (terminalError || uploaderState === "failed") return HEALTH.FAILED;
  if (online === false) return HEALTH.OFFLINE;
  if (paused) return HEALTH.WAITING;
  // Queued (waiting for an upload slot) and finalizing (server assembling
  // chunks) legitimately make no byte progress — exempt them from the stuck
  // rule below or they get flagged POSSIBLY_STUCK after stuckIdleMs.
  if (uploaderState === "queued" || uploaderState === "finalizing") {
    return HEALTH.WAITING;
  }

  const retriesInWindow =
    typeof recentRetryCount === "number" ? recentRetryCount : retryCount;
  if (retriesInWindow >= thresholds.retryFloor) {
    return HEALTH.RETRYING;
  }

  const idleMs = now - (lastProgressAt || now);
  if (idleMs >= thresholds.stuckIdleMs) {
    return HEALTH.POSSIBLY_STUCK;
  }

  if (
    typeof rollingBps === "number" &&
    rollingBps > 0 &&
    rollingBps < thresholds.slowBps &&
    idleMs >= thresholds.slowIdleMs
  ) {
    return HEALTH.SLOW;
  }

  return HEALTH.HEALTHY;
};

export const DEFAULT_THRESHOLDS = Object.freeze({
  stuckIdleMs: 60_000,        // no forward progress for 60 s
  slowIdleMs: 15_000,         // slow only after 15 s of low throughput
  slowBps: 50 * 1024,         // < 50 KB/s
  retryFloor: 3,              // 3+ retries in the window ⇒ Retrying
});

/**
 * Convenience: build the input struct from an UploadDiagnostics instance +
 * network snapshot + telemetry state, so callers don't have to hand-assemble.
 *
 * @param {import('./UploadDiagnostics').UploadDiagnostics} diagnostics
 * @param {object} [telemetryState]   uploadTelemetryService.getState(uploadId)
 * @returns {object} input for computeHealth
 */
export const buildHealthInput = (diagnostics, telemetryState) => {
  const state = diagnostics.getState();
  const now = Date.now();
  const retryEvents = diagnostics.timeline.tail(
    50,
    (entry) =>
      entry.event === "retry_started" || entry.event === "chunk_failed"
  );
  // Recent = within the last 2 minutes
  const cutoff = now - 2 * 60 * 1000;
  const recentRetryCount = retryEvents.filter(
    (e) => e.timestamp >= cutoff
  ).length;

  return {
    now,
    lastProgressAt: state.lastProgressAt || state.lastActivityAt || now,
    retryCount: state.retryCount,
    recentRetryCount,
    rollingBps: telemetryState?.rollingBps,
    online: state.network ? state.network.online !== false : true,
    paused: state.paused,
    terminalError: state.terminalError,
    uploaderState: state.uploaderState,
  };
};
